import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyIcon from '@mui/icons-material/Key';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ProviderService } from '../../services';
import type { ProviderKeyRow } from '../../services/providerService';
import { queryKeys } from '../../queries/keys';
import { SignedOutPanel } from '../../components/auth/SignedOutPanel';
import { useAuthStore } from '../../store/authStore';

/** Vendor-facing names and where to get a key, so the page is actionable. */
const PROVIDER_META: Record<string, { label: string; hint: string }> = {
  FalAI: { label: 'fal.ai', hint: 'Image and video generation · fal.ai/dashboard/keys' },
  Gemini: { label: 'Google Gemini', hint: 'Text and image · aistudio.google.com/apikey' },
  OpenAI: { label: 'OpenAI', hint: 'Text · platform.openai.com/api-keys' },
  Anthropic: { label: 'Anthropic', hint: 'Text · console.anthropic.com/settings/keys' },
  LumaLabs: { label: 'Luma Labs', hint: 'Video · lumalabs.ai' },
};

/**
 * Settings → API keys. Bring your own credentials for any provider.
 *
 * Nothing here can read a stored key back — the API returns only the last four
 * characters, so the page shows which key is stored without ever holding it.
 * Providers without a key fall back to the platform's, which is why this is
 * presented as optional rather than as setup a user must complete.
 */
export const ProviderKeysPage: React.FC = () => {
  const isSignedIn = useAuthStore(state => state.isAuthenticated);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.providerKeys(),
    queryFn: () => ProviderService.listKeys(),
    // Don't ask for a signed-out visitor's keys — the endpoint would 401 and
    // there is nothing to show them but the sign-in panel below.
    enabled: isSignedIn,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.providerKeys() });
    // Availability in the model pickers is derived from whether a key resolves,
    // so it has to refetch when keys change.
    queryClient.invalidateQueries({ queryKey: queryKeys.providerCatalog() });
  };

  const rows = useMemo(() => data?.keys ?? [], [data]);

  // Keys are per-user; without a session there is nothing to list or save.
  if (!isSignedIn) {
    return (
      <Box sx={{ height: '100vh', bgcolor: 'background.default' }}>
        <SignedOutPanel
          what="your API keys"
          detail="Bring your own provider keys once you have an account — generations then run on your key."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', overflowY: 'auto', bgcolor: 'background.default', pb: 8 }}>
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <KeyIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            API keys
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
          Use your own provider account instead of ours. Generations run on your key and are
          billed to you by the provider — and they don&apos;t count against your usage here.
          Leave a provider blank to keep using the platform&apos;s key.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Couldn&apos;t load your keys. Reload to try again.
          </Alert>
        )}

        <Stack spacing={1.5}>
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={92} />
              ))
            : rows.map(row => (
                <ProviderKeyCard key={row.providerId} row={row} onChanged={invalidate} />
              ))}
        </Stack>
      </Container>
    </Box>
  );
};

const ProviderKeyCard: React.FC<{ row: ProviderKeyRow; onChanged: () => void }> = ({
  row,
  onChanged,
}) => {
  const [value, setValue] = useState('');
  const meta = PROVIDER_META[row.providerId] ?? { label: row.providerId, hint: '' };

  const save = useMutation({
    mutationFn: () => ProviderService.saveKey(row.providerId, value.trim()),
    onSuccess: () => {
      setValue('');
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => ProviderService.deleteKey(row.providerId),
    onSuccess: onChanged,
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => ProviderService.setKeyEnabled(row.providerId, enabled),
    onSuccess: onChanged,
  });

  const busy = save.isPending || remove.isPending || toggle.isPending;
  const failed = save.error || remove.error || toggle.error;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {meta.label}
        </Typography>
        {row.hasKey ? (
          <Chip
            size="small"
            label={row.enabled ? `Using your key ····${row.lastFour}` : `Paused ····${row.lastFour}`}
            color={row.enabled ? 'success' : 'default'}
            variant="outlined"
            sx={{ height: 22, fontSize: 11 }}
          />
        ) : (
          <Chip
            size="small"
            label="Using platform key"
            variant="outlined"
            sx={{ height: 22, fontSize: 11 }}
          />
        )}
      </Stack>
      {meta.hint && (
        <Typography variant="caption" color="text.secondary">
          {meta.hint}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          type="password"
          autoComplete="off"
          placeholder={row.hasKey ? 'Enter a new key to replace it' : 'Paste your API key'}
          value={value}
          onChange={e => setValue(e.target.value)}
          disabled={busy}
          inputProps={{ 'aria-label': `${meta.label} API key` }}
        />
        <Button
          variant="contained"
          disabled={busy || value.trim().length === 0}
          onClick={() => save.mutate()}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          {row.hasKey ? 'Replace' : 'Save'}
        </Button>
        {row.hasKey && (
          <>
            <Tooltip title={row.enabled ? 'Pause — fall back to the platform key' : 'Resume using your key'}>
              <Switch
                checked={row.enabled}
                onChange={(_, checked) => toggle.mutate(checked)}
                disabled={busy}
                inputProps={{ 'aria-label': `Use my ${meta.label} key` }}
              />
            </Tooltip>
            <Tooltip title="Remove this key">
              <span>
                <IconButton
                  onClick={() => remove.mutate()}
                  disabled={busy}
                  aria-label={`Remove ${meta.label} key`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>

      {failed && (
        <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
          That didn&apos;t save. Check the key and try again.
        </Typography>
      )}
    </Box>
  );
};
